//
// DISCLAIMER
//
// Copyright 2019-2026 ArangoDB GmbH, Cologne, Germany
//

import moment from "moment";
import React, { useState } from "react";
import { DateRangePicker, FocusedInputShape } from "react-dates";
import { Checkbox, CheckboxProps, Dropdown, DropdownProps, Input, InputOnChangeData, Menu } from "semantic-ui-react";

type UsageItemFilterViewArgs = {
  onDatesChange: (args: { startDate: moment.Moment | null; endDate: moment.Moment | null }) => void;
  onKindChange: (kind: string) => void;
  onDeploymentIDChange: (deploymentID: string) => void;
  onInvoiceIDChange: (invoiceID: string) => void;
  onHideEmptyChange: (hideEmpty: boolean) => void;
  selectedKind: string;
  deploymentID: string;
  invoiceID: string;
  hideEmpty: boolean;
  fromDate: Date;
  toDate: Date;
};

// Billing 2.0 usage kinds that map to CreditPlan components. Exported so the
// CreditPlan calculator can reuse exactly the same list (single source of truth).
//
// KEEP IN SYNC with the canonical Go list `BillingV2UsageItemKinds` in
// apis/usage/v1/kinds.go — the `value` of each entry must equal a UsageItemKindV2*
// constant. This cannot be imported across the Go/TS boundary (kinds.go is
// hand-written, not generated), so adding a new V2 kind there means adding it here too.
export const creditUsageKindOptions = [
  { key: "cpuHour", value: "CPUHour", text: "CPU Hour" },
  { key: "memoryHour", value: "MemoryHour", text: "Memory Hour" },
  { key: "storageHour", value: "StorageHour", text: "Storage Hour" },
  { key: "storagePerformanceHour", value: "StoragePerformanceHour", text: "Storage Performance Hour" },
  { key: "gpuHour", value: "GPUHour", text: "GPU Hour" },
  { key: "deploymentAEU", value: "DeploymentAEU", text: "Deployment AEU" },
  { key: "networkSize", value: "NetworkUsageSize", text: "Network Size" },
  { key: "cloudStorageHour", value: "CloudStorageHour", text: "Cloud Storage Hour" },
  { key: "auditLogRequests", value: "AuditLogRequests", text: "Audit Log Requests" },
  { key: "auditLogData", value: "AuditLogData", text: "Audit Log Data" },
  { key: "addonHour", value: "AddonHour", text: "Addon Hour" },
];

const usageKindOptions = [
  { key: "all", value: "all", text: "All" },
  // All Billing 2.0 kinds at once (excludes deprecated/legacy kinds). Applied client-side.
  { key: "allV2", value: "allV2", text: "All V2" },

  // Billing 2.0 usage kinds.
  ...creditUsageKindOptions,

  // Legacy usage kinds.
  { key: "deploymentSize", value: "DeploymentSize", text: "(deprecated) Deployment Size" },
  { key: "networkTransferSize", value: "NetworkTransferSize", text: "(deprecated) Network Transfer Size" },
  { key: "backupStorageSize", value: "BackupStorageSize", text: "(deprecated) Backup Storage Size" },
  { key: "auditLogSize", value: "AuditLogSize", text: "(deprecated) Audit Log Size" },
  { key: "auditLogStorageSize", value: "AuditLogStorageSize", text: "(deprecated) AuditLog Storage Size" },
  { key: "mlServicesSize", value: "MLServicesSize", text: "(deprecated) MLServices Size" },
  { key: "notebookSize", value: "NotebookSize", text: "(deprecated) Notebook Size" },
];

// "Hide empty" only means what it says for Billing 2.0 kinds: the server's
// exclude_empty filter (usaged's buildUsageItemFilter) only checks the V2
// quantity leaves (cpu_hour.cpu_hours, memory_hour.memory_gib_hours, ...) -
// a V1/legacy item never has any of those, so it is unconditionally treated
// as "empty" and hidden, non-empty or not, whenever exclude_empty is set.
// "all" (mixes V1 and V2) and any single legacy kind therefore cannot use
// it without silently dropping real V1 usage - only "allV2" or a single V2
// kind can. Exported so UsageItemList's buildUsageItemsRequest can gate the
// actual exclude_empty it sends the same way, not just this checkbox's
// visibility - see the comment there.
export const isV2OnlyKind = (kind: string): boolean => kind === "allV2" || creditUsageKindOptions.some((option) => option.value === kind);

const UsageItemFilterView = ({
  onDatesChange,
  onKindChange,
  onDeploymentIDChange,
  onInvoiceIDChange,
  onHideEmptyChange,
  selectedKind,
  deploymentID,
  hideEmpty,
  fromDate,
  toDate,
  invoiceID,
}: UsageItemFilterViewArgs) => {
  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(null);

  const handleHideEmptyChange = (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
    onHideEmptyChange(!!data.checked);
  };

  const onDatesChanged = (args: { startDate: moment.Moment | null; endDate: moment.Moment | null }) => {
    if (args.startDate && args.endDate) {
      onDatesChange(args);
    }
  };

  const handleDeploymentIdChange = (event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
    const { value = "" } = data;
    onDeploymentIDChange(value);
  };

  const handleInvoiceIDChange = (event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
    const { value = "" } = data;
    onInvoiceIDChange(value);
  };

  return (
    <Menu borderless pointing stackable>
      <Menu.Item header>Filter</Menu.Item>
      <Dropdown item text="Search">
        <Dropdown.Menu>
          <Menu.Item>
            <Input
              icon="search"
              iconPosition="left"
              placeholder="Deployment ID"
              name="search"
              value={deploymentID}
              onClick={(event: React.ChangeEvent<HTMLInputElement>) => event.stopPropagation()}
              onChange={handleDeploymentIdChange}
            />
          </Menu.Item>
          <Menu.Item>
            <Input
              icon="search"
              iconPosition="left"
              placeholder="Invoice ID"
              name="search"
              value={invoiceID}
              onClick={(event: any) => event.stopPropagation()}
              onChange={handleInvoiceIDChange}
            />
          </Menu.Item>
        </Dropdown.Menu>
      </Dropdown>
      <Menu.Item>
        <Menu.Item>Usage : </Menu.Item>
        <DateRangePicker
          noBorder
          small
          startDate={moment(fromDate)}
          startDateId="usageItemListStartDate"
          endDate={moment(toDate)}
          endDateId="usageItemListEndDate"
          onDatesChange={onDatesChanged}
          isOutsideRange={() => false}
          focusedInput={focusedInput}
          onFocusChange={(focusedInput) => setFocusedInput(focusedInput)}
        />
      </Menu.Item>
      <Dropdown
        item
        text={`Kind - ${selectedKind}`}
        value={selectedKind}
        options={usageKindOptions}
        onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
          const { value = "" } = data;
          onKindChange(value as string);
        }}
      />
      {isV2OnlyKind(selectedKind) && (
        <Menu.Item>
          <Checkbox label="Hide empty items" checked={hideEmpty} onChange={handleHideEmptyChange} />
        </Menu.Item>
      )}
    </Menu>
  );
};

export default UsageItemFilterView;
